import React, { useState, useEffect, useRef } from 'react';
import './BatchTab.css';

function BatchTab({ apiUrl }) {
  const [file, setFile] = useState(null);
  const [query, setQuery] = useState('');
  const [businessVertical, setBusinessVertical] = useState('Unknown');
  const [maxResults, setMaxResults] = useState(20);
  const [businessVerticals, setBusinessVerticals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeBatch, setActiveBatch] = useState(null);
  const [batchHistory, setBatchHistory] = useState([]);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const pollIntervalRef = useRef(null);

  // Fetch business verticals and batch history on mount
  useEffect(() => {
    fetchBusinessVerticals();
    fetchBatchHistory();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBusinessVerticals = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/config/business-verticals`);
      if (response.ok) {
        const data = await response.json();
        setBusinessVerticals(data.values || []);
        if (data.values?.length > 0) setBusinessVertical(data.values[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch verticals:', err);
    }
  };

  const fetchBatchHistory = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/batch`);
      if (response.ok) {
        const data = await response.json();
        setBatchHistory(data);
        
        // If there's an active batch, start polling
        const runningBatch = data.find(b => ['running', 'pending', 'stopping'].includes(b.status));
        if (runningBatch) {
          startPolling(runningBatch.id);
        } else if (!activeBatch || ['completed', 'failed', 'stopped', 'interrupted'].includes(activeBatch.status)) {
          setActiveBatch(data[0] || null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const startPolling = (batchId) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${apiUrl}/api/batch/${batchId}`);
        if (response.ok) {
          const data = await response.json();
          setActiveBatch(data);
          
          if (['completed', 'failed', 'stopped', 'interrupted'].includes(data.status)) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            fetchBatchHistory(); // Refresh history
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !query) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('query', query);
    formData.append('businessVertical', businessVertical);
    formData.append('maxResults', maxResults);

    try {
      const response = await fetch(`${apiUrl}/api/batch/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload batch');
      }

      const data = await response.json();
      setSuccess(`Batch uploaded successfully! Processing ${data.totalItems} locations.`);
      setFile(null);
      // Reset file input
      document.getElementById('batch-file').value = '';
      
      startPolling(data.batchId);
      fetchBatchHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed': return 'badge-success';
      case 'running': return 'badge-info';
      case 'pending': return 'badge-warning';
      case 'stopping': return 'badge-warning';
      case 'stopped': return 'badge-secondary';
      case 'interrupted': return 'badge-danger';
      case 'failed': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const canStop = (status) => ['pending', 'running', 'stopping'].includes(status);
  const canResume = (status) => ['interrupted', 'stopped', 'pending', 'failed'].includes(status);
  const canRestart = (status) => ['completed', 'failed', 'stopped', 'interrupted'].includes(status);

  const handleBatchAction = async (batchId, action) => {
    setActionLoadingId(`${action}-${batchId}`);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${apiUrl}/api/batch/${batchId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Failed to ${action} batch`);
      }

      if (action === 'stop') {
        setSuccess('Stop requested. The current location will finish before the batch stops.');
        setActiveBatch(data.batch || activeBatch);
      } else {
        setSuccess(`Batch ${action} started.`);
        startPolling(batchId);
      }

      await fetchBatchHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="batch-tab">
      <div className="batch-header">
        <h1>Batch Processing</h1>
        <p className="subtitle">Upload an Excel file to process multiple locations at once.</p>
      </div>

      <div className="batch-container">
        <div className="batch-upload-section">
          <form className="batch-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="query">Business Type / Query</label>
                <input
                  type="text"
                  id="query"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g., barbers, restaurants"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="businessVertical">Business Vertical</label>
                <select
                  id="businessVertical"
                  value={businessVertical}
                  onChange={(e) => setBusinessVertical(e.target.value)}
                >
                  {businessVerticals.map(v => (
                    <option key={v.id} value={v.id}>{v.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="batch-file">Excel File (city, zipcode, state)</label>
                <input
                  type="file"
                  id="batch-file"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="maxResults">Max Results per Location</label>
                <input
                  type="number"
                  id="maxResults"
                  value={maxResults}
                  onChange={(e) => setMaxResults(e.target.value)}
                  min="1"
                  max="100"
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading || !file || !query}>
              {loading ? 'Uploading...' : 'Start Batch Process'}
            </button>

            {error && <div className="error-message mt-10">{error}</div>}
            {success && <div className="success-message mt-10">{success}</div>}
          </form>
        </div>

        {activeBatch && (
          <div className="active-batch-section">
            <h3>Active Batch: {activeBatch.filename}</h3>
            <div className="progress-container">
              <div 
                className="progress-bar" 
                style={{ width: `${(activeBatch.processed_items / activeBatch.total_items) * 100}%` }}
              ></div>
            </div>
            <div className="progress-stats">
              <span>{activeBatch.processed_items} / {activeBatch.total_items} locations processed</span>
              <span className={`badge ${getStatusBadgeClass(activeBatch.status)}`}>
                {activeBatch.status.toUpperCase()}
              </span>
            </div>
            <div className="batch-actions">
              {canStop(activeBatch.status) && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={actionLoadingId === `stop-${activeBatch.id}` || activeBatch.status === 'stopping'}
                  onClick={() => handleBatchAction(activeBatch.id, 'stop')}
                >
                  {activeBatch.status === 'stopping' ? 'Stopping...' : 'Stop Job'}
                </button>
              )}
              {canResume(activeBatch.status) && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={actionLoadingId === `resume-${activeBatch.id}`}
                  onClick={() => handleBatchAction(activeBatch.id, 'resume')}
                >
                  Resume Job
                </button>
              )}
              {canRestart(activeBatch.status) && (
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={actionLoadingId === `restart-${activeBatch.id}`}
                  onClick={() => handleBatchAction(activeBatch.id, 'restart')}
                >
                  Restart Job
                </button>
              )}
            </div>
            {activeBatch.last_error && (
              <p className="batch-note">Last issue: {activeBatch.last_error}</p>
            )}
          </div>
        )}

        <div className="batch-history-section">
          <h3>Batch History</h3>
          <div className="table-responsive">
            <table className="batch-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Filename</th>
                  <th>Query</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batchHistory.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">No batch jobs found</td>
                  </tr>
                ) : (
                  batchHistory.map(batch => (
                    <tr key={batch.id}>
                      <td>{new Date(batch.created_at).toLocaleString()}</td>
                      <td>{batch.filename}</td>
                      <td>{batch.query}</td>
                      <td>{batch.processed_items} / {batch.total_items}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(batch.status)}`}>
                          {batch.status}
                        </span>
                      </td>
                      <td>
                        <div className="batch-table-actions">
                          {canStop(batch.status) && (
                            <button
                              type="button"
                              className="btn btn-small btn-secondary"
                              disabled={actionLoadingId === `stop-${batch.id}` || batch.status === 'stopping'}
                              onClick={() => handleBatchAction(batch.id, 'stop')}
                            >
                              Stop
                            </button>
                          )}
                          {canResume(batch.status) && (
                            <button
                              type="button"
                              className="btn btn-small btn-primary"
                              disabled={actionLoadingId === `resume-${batch.id}`}
                              onClick={() => handleBatchAction(batch.id, 'resume')}
                            >
                              Resume
                            </button>
                          )}
                          {canRestart(batch.status) && (
                            <button
                              type="button"
                              className="btn btn-small btn-outline"
                              disabled={actionLoadingId === `restart-${batch.id}`}
                              onClick={() => handleBatchAction(batch.id, 'restart')}
                            >
                              Restart
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BatchTab;
